import { StreamingPlayerError } from '@heardis/stream-contracts';
import { useContext, useReducer, createContext, useEffect } from 'react';
import { disableAnalytics, enableAnalytics, getHdisAnalyticsCookieValue, unsetAnalyticsPreferences } from '../utils/analytics';
import { Logger } from '../utils/logger';

const logger = new Logger('SettingsContext');

type SettingsContextError = {
  code: StreamingPlayerError;
  message: string;
} | null;

export enum SettingsContextAnalyticsStatus {
  ENABLED = 'enabled',
  DISABLED = 'disabled',
  NOT_SET = 'not_set',
}

export type SettingsContextProps = {
  cache: boolean;
  analytics: SettingsContextAnalyticsStatus;
  error: SettingsContextError;
};

export type SettingsContextProviderProps = {
  children: React.ReactNode;
};

export enum SettingsContextAction {
  SET_CACHE = 'SET_CACHE',
  SET_ANALYTICS = 'SET_ANALYTICS',
  SET_ERROR = 'SET_ERROR',
}

type Action =
  | { type: SettingsContextAction.SET_CACHE; cache: boolean }
  | { type: SettingsContextAction.SET_ANALYTICS; analytics: SettingsContextAnalyticsStatus }
  | { type: SettingsContextAction.SET_ERROR; error: SettingsContextError };

export const defaultSettings: SettingsContextProps = {
  cache: true,
  analytics: SettingsContextAnalyticsStatus.NOT_SET,
  error: null,
};

const SettingsContext = createContext<any>(defaultSettings);

export const settingsReducer = (state: SettingsContextProps, action: Action): SettingsContextProps => {
  switch (action.type) {
    case SettingsContextAction.SET_CACHE:
      return { ...state, cache: action.cache };
    case SettingsContextAction.SET_ANALYTICS:
      return { ...state, analytics: action.analytics };
    case SettingsContextAction.SET_ERROR:
      return { ...state, error: action.error };
    default:
      logger.warn('Unhandled action:', action);
      return state;
  }
};

function SettingsContextProvider({ children }: SettingsContextProviderProps): JSX.Element {
  const [state, dispatch] = useReducer(settingsReducer, defaultSettings);
  // NOTE: you *might* need to memoize this value
  // Learn more in http://kcd.im/optimize-context

  const setError = (error: SettingsContextError) => {
    logger.debug('set error', error);
    /**
     * @TOASK I would have added trackAnalytics here but it causes a circular dependency error
     * It's because useAnalytics checks if analytics is enabled from settings context
     * does it make sense to just implement the analytics hook here?
    */
    dispatch({ type: SettingsContextAction.SET_ERROR, error });
  };

  const setAnalytics = (analytics: SettingsContextAnalyticsStatus) => {
    logger.debug('set analytics', analytics);
    dispatch({ type: SettingsContextAction.SET_ANALYTICS, analytics });
  };

  useEffect(() => {
    const hdisAnalyticsCookie = getHdisAnalyticsCookieValue();
    if (hdisAnalyticsCookie && hdisAnalyticsCookie === 'true') {
      setAnalytics(SettingsContextAnalyticsStatus.ENABLED);
    } else if (hdisAnalyticsCookie && hdisAnalyticsCookie === 'false') {
      setAnalytics(SettingsContextAnalyticsStatus.DISABLED);
    }
  }, []);

  useEffect(() => {
    switch (state.analytics) {
      case SettingsContextAnalyticsStatus.ENABLED:
        enableAnalytics();
        break;
      case SettingsContextAnalyticsStatus.DISABLED:
        disableAnalytics();
        break;
      case SettingsContextAnalyticsStatus.NOT_SET:
        unsetAnalyticsPreferences();
        break;
      default: logger.warn('unhandled analytics status:', state.analytics);
    }
  }, [state.analytics]);

  return (
    <SettingsContext.Provider value={{ ...state, setError, setAnalytics, dispatch }}>
      {children}
    </SettingsContext.Provider>
  );
}

type UseSettingsContextProps = {
  dispatch: React.Dispatch<Action>;
  setError: (error: SettingsContextError) => void;
  setAnalytics: (analytics: SettingsContextAnalyticsStatus) => void;
} & SettingsContextProps;

function useSettingsContext(): UseSettingsContextProps {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}

export { SettingsContextProvider, useSettingsContext };
